import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { CalendarDays, Database, LogOut, MapPin, Menu, Radio, Users, X } from "lucide-react";

import { abmelden, anmelden, benutzer, laden, rueckkehrVerarbeiten } from "@/auth.ts";
import { TagSeite } from "@/seiten/Tag.tsx";
import { OrteSeite } from "@/seiten/Orte.tsx";
import { ArchivSeite } from "@/seiten/Archiv.tsx";
import { LiveSeite } from "@/seiten/Live.tsx";
import { PersonenSeite } from "@/seiten/Personen.tsx";
import { Frische } from "@/komponenten/Frische.tsx";
import { ICloudHinweis } from "@/komponenten/ICloudAnmeldung.tsx";
import { Rueckmeldung } from "@/komponenten/Rueckmeldung.tsx";

export function App() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<Rueckkehr />} />
      <Route path="/*" element={<Geschuetzt />} />
    </Routes>
  );
}

/** Rückkehr von MCPAuth. */
function Rueckkehr() {
  const navigieren = useNavigate();
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    rueckkehrVerarbeiten()
      .then((ziel) => navigieren(ziel, { replace: true }))
      .catch((e: unknown) => {
        // Kein gespeicherter Zustand → veralteter Callback (z.B. Autocomplete
        // des Browsers). Frisch anmelden statt Fehler zeigen.
        if (!sessionStorage.getItem("ribbon.zustand")) {
          navigieren("/", { replace: true });
          return;
        }
        setFehler(e instanceof Error ? e.message : String(e));
      });
  }, [navigieren]);

  if (fehler) {
    return (
      <Mitteilung titel="Anmeldung fehlgeschlagen">
        <p className="text-text-muted">{fehler}</p>
        <button onClick={() => void anmelden()} className="mt-6 rounded-lg bg-primary px-5 py-2.5 text-white hover:bg-primary-dark">
          Noch einmal versuchen
        </button>
      </Mitteilung>
    );
  }

  return <Mitteilung titel="Anmeldung läuft…" />;
}

function Geschuetzt() {
  const [sitzung, setSitzung] = useState(() => laden());

  useEffect(() => {
    if (!sitzung) void anmelden();
  }, [sitzung]);

  // Läuft die Sitzung während der Nutzung ab, meldet die API das über ein
  // Ereignis, statt die Seite mit einem leeren Zustand stehenzulassen.
  useEffect(() => {
    const abgelaufen = () => setSitzung(null);
    window.addEventListener("ribbon:abgemeldet", abgelaufen);
    return () => window.removeEventListener("ribbon:abgemeldet", abgelaufen);
  }, []);

  if (!sitzung) {
    return (
      <Mitteilung titel="Ribbon">
        <p className="text-text-muted">Weiterleitung zur Anmeldung…</p>
      </Mitteilung>
    );
  }

  return <Rahmen />;
}

const NAVIGATION = [
  { pfad: "/tag", beschriftung: "Zeitachse", Symbol: CalendarDays },
  { pfad: "/orte", beschriftung: "Orte", Symbol: MapPin },
  { pfad: "/archiv", beschriftung: "Archiv", Symbol: Database },
  { pfad: "/live", beschriftung: "Live", Symbol: Radio },
  { pfad: "/personen", beschriftung: "Personen", Symbol: Users },
];

function Rahmen() {
  const [navOffen, setNavOffen] = useState(false);
  const person = benutzer();

  const initialen =
    person?.name
      .split(/\s+/)
      .slice(0, 2)
      .map((teil) => teil[0]?.toUpperCase() ?? "")
      .join("") || "?";

  return (
    <div className="flex h-full">
      {/* Navigation. Auf schmalen Geräten fährt sie über den Inhalt. */}
      <nav
        className={`fixed inset-y-0 left-0 z-40 flex w-60 flex-col bg-sidebar text-slate-300 transition-transform lg:static lg:translate-x-0 ${
          navOffen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-2 px-5 py-5 text-white">
          <span className="text-lg font-semibold tracking-tight">Ribbon</span>
          <button
            onClick={() => setNavOffen(false)}
            className="ml-auto rounded p-1 hover:bg-sidebar-hover lg:hidden"
            aria-label="Navigation schließen"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-1 px-3">
          {NAVIGATION.map(({ pfad, beschriftung, Symbol }) => (
            <NavLink
              key={pfad}
              to={pfad}
              onClick={() => setNavOffen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-primary text-white" : "hover:bg-sidebar-hover"
                }`
              }
            >
              <Symbol size={18} />
              {beschriftung}
            </NavLink>
          ))}
        </div>

        <Rueckmeldung />

        <ICloudHinweis />

        <Frische />

        <div className="border-t border-slate-700 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
              {initialen}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-white">{person?.name}</span>
              <span className="block truncate text-xs text-slate-400">{person?.role}</span>
            </span>
            <button
              onClick={abmelden}
              className="rounded p-1.5 hover:bg-sidebar-hover"
              title="Abmelden"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </nav>

      {navOffen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setNavOffen(false)}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <button
          onClick={() => setNavOffen(true)}
          className="fixed top-3 left-3 z-20 rounded-lg bg-surface p-2 shadow-md lg:hidden"
          aria-label="Navigation öffnen"
        >
          <Menu size={20} />
        </button>

        <main className="min-h-0 flex-1 overflow-hidden">
          <Routes>
            <Route path="/" element={<Navigate to="/tag" replace />} />
            <Route path="/tag" element={<TagSeite />} />
            <Route path="/orte" element={<OrteSeite />} />
            <Route path="/archiv" element={<ArchivSeite />} />
            <Route path="/live" element={<LiveSeite />} />
            <Route path="/personen" element={<PersonenSeite />} />
            <Route path="*" element={<Navigate to="/tag" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function Mitteilung({ titel, children }: { titel: string; children?: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="text-center">
        <h1 className="text-2xl font-semibold">{titel}</h1>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}
